export type ActivityListCustomizations = {
  show_closed_ticket_toggle?: boolean;
  show_create_types?: string[];
  filters?: string[];
};

export type ActivityDetailsCustomizations = {
  editable_ticket_fields?: boolean;
  cascading_taxonomy_selects?: boolean;
  product_search?: {
    enabled?: boolean;
  };
};

export type ActivityFieldVisibility = {
  visible_when?: Record<string, string | number | boolean>;
};

export type ActivityCustomizations = {
  list?: ActivityListCustomizations;
  details?: ActivityDetailsCustomizations;
  fields?: {
    last_date?: ActivityFieldVisibility;
    product_id?: ActivityFieldVisibility;
    [key: string]: ActivityFieldVisibility | undefined;
  };
};

export const getActivityCustomizations = (
  company: any
): ActivityCustomizations | undefined => company?.customizations?.activities;

// ---------------------------------------------------------------------------
// Orders customizations
// ---------------------------------------------------------------------------

export type OrdersListCustomizations = {
  show_total_count?: boolean;
  filters?: string[];
  print_receipt?: { open_a4_modal?: boolean };
  features?: { live_refresh?: boolean };
};

export type OrdersDetailsActionKey =
  | "add_product"
  | "edit_product"
  | "delete_product"
  | "add_discount"
  | "refund"
  | "send_invoice_link";

export type OrdersDetailsCustomizations = {
  actions?: Partial<Record<OrdersDetailsActionKey, boolean>>;
  online_order_id?: { visible?: boolean };
  receipt_number?: { visible?: boolean };
  total_after_discount?: { visible?: boolean };
  insurance_delivery_at?: { visible?: boolean };
  map_location?: { visible?: boolean };
  unit_price?: { readonly?: boolean };
  agent_comment_label?: "customer_service_comment" | "application_comment";
  product_search?: {
    image_base_url?: string | null;
    allow_zero_price?: boolean;
  };
};

export type OrdersCustomizations = {
  list?: OrdersListCustomizations;
  details?: OrdersDetailsCustomizations;
};

export const getOrdersCustomizations = (
  company: any
): OrdersCustomizations | undefined => company?.customizations?.orders;

/**
 * When `details.actions` is absent, fall back to permission-only.
 * When present, require both the flag and the permission.
 */
export const isOrderActionEnabled = (
  orders: OrdersCustomizations | undefined,
  action: OrdersDetailsActionKey,
  hasPerm: boolean
): boolean => {
  const actions = orders?.details?.actions;
  if (!actions) return hasPerm;
  return !!actions[action] && hasPerm;
};

/**
 * Field visibility when the details slice is present.
 * Absent customizations / field config → visible (legacy behavior).
 */
export const isOrderDetailFieldVisible = (
  orders: OrdersCustomizations | undefined,
  field: keyof Omit<
    OrdersDetailsCustomizations,
    "actions" | "unit_price" | "agent_comment_label" | "product_search"
  >
): boolean => {
  const details = orders?.details;
  if (!details) return true;
  const config = details[field];
  if (config == null) return true;
  return !!(config as { visible?: boolean }).visible;
};

export const DEFAULT_ORDER_LIST_FILTERS = [
  "from",
  "to",
  "delivery_date_from",
  "delivery_date_to",
  "payment_method",
  "order_status_id",
  "store_id",
  "order_creator",
  "created_from",
  "customer_phone",
  "customer_name",
  "productBarcode",
  "call_channel",
  "area_id",
  "region_id",
  "total",
] as const;

// ---------------------------------------------------------------------------
// POS customizations
// ---------------------------------------------------------------------------

export type PosAddressFieldKey =
  | "building_number"
  | "street_name"
  | "floor"
  | "apartment_number"
  | "landmark";

export type PosAddressFieldRule = {
  required_on_delivery: boolean;
};

export type PosWorkflowSlice = {
  show_delivery_order_select: boolean;
  show_store_select: boolean;
  cart_customer_id: -1 | "selected_customer";
  guest_id_generation: "cashier_epoch" | "none";
  default_product_tab: "store_most_sales" | "customer_most_products";
  post_checkout_reset: "silent" | "prompt_new_order";
  pickup_source: "delivery_order_select" | "order_delivery_type";
  show_customer_info_after_store?: boolean;
};

export type PosCustomizations = {
  workflow: {
    cashier: PosWorkflowSlice;
    agent: PosWorkflowSlice;
  };
  store_selection: {
    default_mode: "region" | "distance";
    allow_mode_toggle: boolean;
    distance: {
      require_address_coordinates: boolean;
      auto_select_nearest: boolean;
      label_format: string;
    };
  };
  delivery: {
    pickup_zeroes_fee: boolean;
    fixed_fee_options: number[] | null;
  };
  stock: {
    allow_out_of_stock_orders: boolean;
    enforce_on_cart_increment: boolean;
    level_thresholds: {
      enabled: boolean;
      level_1: number | null;
      level_2: number | null;
      level_3: number | null;
    };
  };
  address: {
    use_city_select: boolean;
    load_areas_per_city: boolean;
    map_provider: "default" | "sa";
    fields: Record<PosAddressFieldKey, PosAddressFieldRule>;
  };
  checkout: {
    require_delivery_time: boolean;
    require_call_channel: boolean;
    require_delivery_date: boolean;
    payment_requires_positive_total: boolean;
    allow_partial_payment_total: boolean;
  };
  features: {
    offers: boolean;
    free_text_products: boolean;
    uncompleted_orders: boolean;
    duplicate_order_at_store_warning: boolean;
    existing_cart_warning: boolean;
    devtools_protection: boolean;
  };
};

/** Runtime workflow from GET pos/context (role resolved). */
export type PosResolvedWorkflow = PosWorkflowSlice & {
  role: "cashier" | "agent";
  is_cashier: boolean;
};

/** Success body of GET pos/context. */
export type PosContextResponse = {
  workflow: PosResolvedWorkflow;
  store_selection: PosCustomizations["store_selection"];
  delivery: PosCustomizations["delivery"];
  stock: PosCustomizations["stock"];
  address: PosCustomizations["address"];
  checkout: PosCustomizations["checkout"];
  features: PosCustomizations["features"];
  guest_id: number;
};

export type PosStoreRow = {
  id: number;
  name_en?: string;
  name_ar?: string;
  name?: string;
  latitude?: number;
  longitude?: number;
  shipping_fees?: number;
  delivery_type?: number;
  distance_km?: number | null;
  label?: string;
  auto_selected?: boolean;
  [key: string]: unknown;
};

export const DEFAULT_POS_CUSTOMIZATIONS: PosCustomizations = {
  workflow: {
    cashier: {
      show_delivery_order_select: true,
      show_store_select: false,
      cart_customer_id: -1,
      guest_id_generation: "cashier_epoch",
      default_product_tab: "store_most_sales",
      post_checkout_reset: "silent",
      pickup_source: "delivery_order_select",
    },
    agent: {
      show_delivery_order_select: false,
      show_store_select: true,
      cart_customer_id: "selected_customer",
      guest_id_generation: "none",
      default_product_tab: "customer_most_products",
      post_checkout_reset: "prompt_new_order",
      pickup_source: "order_delivery_type",
      show_customer_info_after_store: true,
    },
  },
  store_selection: {
    default_mode: "region",
    allow_mode_toggle: false,
    distance: {
      require_address_coordinates: true,
      auto_select_nearest: true,
      label_format: "{name} ({distance_km} كم)",
    },
  },
  delivery: {
    pickup_zeroes_fee: true,
    fixed_fee_options: null,
  },
  stock: {
    allow_out_of_stock_orders: false,
    enforce_on_cart_increment: true,
    level_thresholds: {
      enabled: false,
      level_1: null,
      level_2: null,
      level_3: null,
    },
  },
  address: {
    use_city_select: false,
    load_areas_per_city: false,
    map_provider: "default",
    fields: {
      building_number: { required_on_delivery: true },
      street_name: { required_on_delivery: true },
      floor: { required_on_delivery: true },
      apartment_number: { required_on_delivery: true },
      landmark: { required_on_delivery: false },
    },
  },
  checkout: {
    require_delivery_time: true,
    require_call_channel: true,
    require_delivery_date: true,
    payment_requires_positive_total: true,
    allow_partial_payment_total: true,
  },
  features: {
    offers: true,
    free_text_products: true,
    uncompleted_orders: true,
    duplicate_order_at_store_warning: true,
    existing_cart_warning: true,
    devtools_protection: false,
  },
};

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

/** Shallow-recursive merge; arrays and nulls from source replace defaults. */
function deepMergePos<T extends Record<string, any>>(
  defaults: T,
  override: Record<string, any> | null | undefined
): T {
  if (!override || !isPlainObject(override)) {
    return { ...defaults };
  }

  const result: Record<string, any> = { ...defaults };
  for (const key of Object.keys(override)) {
    const next = override[key];
    const prev = defaults[key as keyof T];
    if (Array.isArray(next) || next === null || typeof next !== "object") {
      result[key] = next;
    } else if (isPlainObject(prev) && isPlainObject(next)) {
      result[key] = deepMergePos(prev as Record<string, any>, next);
    } else {
      result[key] = next;
    }
  }
  return result as T;
}

/**
 * Returns company POS customizations merged over safe agent-like defaults.
 * Does not resolve cashier vs agent — use GET pos/context for that.
 */
export const getPosCustomizations = (company: any): PosCustomizations =>
  deepMergePos(DEFAULT_POS_CUSTOMIZATIONS, company?.customizations?.pos);

/** Normalize InfiniteSelect / plain values for comparisons. */
export const extractCustomizationValue = (val: any): any => {
  if (val && typeof val === "object" && val.value !== undefined) {
    return val.value;
  }
  return val;
};

/**
 * Returns true when the field has no visible_when rule, or when every
 * visible_when entry matches the current form/context values.
 */
export const isCustomizationFieldVisible = (
  fieldConfig: ActivityFieldVisibility | undefined,
  context: Record<string, any>
): boolean => {
  const visibleWhen = fieldConfig?.visible_when;
  if (!visibleWhen || Object.keys(visibleWhen).length === 0) {
    return true;
  }

  return Object.entries(visibleWhen).every(([key, expected]) => {
    const actual = extractCustomizationValue(context[key]);
    if (actual === undefined || actual === null || actual === "") {
      return false;
    }
    return String(actual) === String(expected);
  });
};

export const DEFAULT_ACTIVITY_CREATE_TYPES = [
  "createInquiry",
  "createActivity",
  "createComplaint",
] as const;

export type ActivityCreateType =
  (typeof DEFAULT_ACTIVITY_CREATE_TYPES)[number];

/**
 * Resolve which create-menu entries to show.
 * Empty / unknown values fall back to the full default set so "+ Add New"
 * never opens an empty dropdown.
 */
export const resolveActivityCreateTypes = (
  showCreateTypes?: string[] | null
): ActivityCreateType[] => {
  const allowed = new Set<string>(DEFAULT_ACTIVITY_CREATE_TYPES);
  const filtered = (showCreateTypes ?? []).filter((type): type is ActivityCreateType =>
    allowed.has(type)
  );
  return filtered.length > 0 ? filtered : [...DEFAULT_ACTIVITY_CREATE_TYPES];
};

export const DEFAULT_ACTIVITY_LIST_FILTERS = [
  "search_customer",
  "search_activity",
  "type",
  "search_product",
  "created_from",
  "created_to",
  "store",
  "status",
] as const;
